import { createContextAttachment } from '@multiplayer-app/ai-agent-react'
import type { AgentAttachment } from '@multiplayer-app/ai-agent-types'
import { checklistItemUrl } from '@/shared/config/endpoints'
import type { ScanResult } from './types'

/** A resolved result paired with its display name, for attachment building. */
export interface NamedScanResult {
  result: ScanResult
  name: string
}

// Leading description so the agent understands what the context is and the
// likely intent — verdicts and "matched language" are otherwise opaque.
const DESCRIBE_ONE =
  'Ramp checklist-item compliance scan result for the open Word document. ' +
  'Verdict Pass = the document satisfies this checklist item; Fail = it does not. ' +
  '"Matched language" is the verbatim document text the scan flagged. ' +
  'The user shared this to get help reviewing, explaining, or revising the document to address the finding. ' +
  'If the user asks to suggest, recommend, or propose fixes, they expect a review-gated Word Apply proposal: ' +
  'analyze the finding, read the live target text, then call one safe Word write tool for the first actionable fix.'

const DESCRIBE_RUN =
  'Ramp checklist-item compliance scan results for the open Word document (multiple items). ' +
  'Per item, Verdict Pass = the document satisfies it; Fail = it does not, and "Matched language" is the ' +
  'verbatim document text the scan flagged. The user shared the whole run to get help reviewing, explaining, ' +
  'or revising the document to address the findings (typically the failed ones). If the user asks to suggest, ' +
  'recommend, or propose fixes, they expect one review-gated Word proposal: analyze all failures briefly, read ' +
  'the live target for each actionable fix, then call applyWordEdits ONCE with every fix as its own edit ' +
  '(tracked changes), falling back to a single-edit tool only when just one item is actionable.'

const findingsOf = (result: ScanResult): string[] =>
  (result.textBlocks ?? []).map((block) => block.text).filter(Boolean)

/** Human-readable lines for one result (item, verdict, explanation, findings). */
function resultLines(result: ScanResult, name: string): string[] {
  const lines = [`Checklist item: ${name}`]
  if (result.scanStatus) lines.push(`Verdict: ${result.scanStatus}`)
  if (result.explanation) lines.push(`Explanation: ${result.explanation}`)
  const findings = findingsOf(result)
  if (findings.length) {
    lines.push('Matched language:')
    findings.forEach((text) => lines.push(`- ${text}`))
  }
  return lines
}

// Lead with the actionable verdicts: the backend clips the model-facing summary,
// so failures (then errors) must come before passes to survive truncation and to
// focus the agent on what needs fixing.
const VERDICT_RANK: Record<string, number> = { Fail: 0, Error: 1, Pass: 2 }
const verdictRank = (result: ScanResult): number => VERDICT_RANK[result.scanStatus ?? ''] ?? 3
const orderByVerdict = (items: NamedScanResult[]): NamedScanResult[] =>
  [...items].sort((a, b) => verdictRank(a.result) - verdictRank(b.result))

/** Structured record for one result (kept in attachment data). */
const resultData = (result: ScanResult, name: string) => ({
  checklistItem: name,
  checklistItemId: result.checklistItemId,
  verdict: result.scanStatus ?? null,
  explanation: result.explanation ?? null,
  findings: findingsOf(result)
})

/**
 * The backend's generic context renderer (ai-agent-node AIHelper) emits `title`
 * + `summary` for custom kinds and SKIPS `data` once a summary is present — so
 * the model-facing content lives in `summary`; `data` is kept as a structured
 * record for completeness.
 */

/** One checklist-item scan result as a chat context attachment. */
export function buildScanResultAttachment(result: ScanResult, name: string, documentName?: string): AgentAttachment {
  const label = `Scan result · ${name}`
  const summary = [
    DESCRIBE_ONE,
    '',
    ...(documentName ? [`Document: ${documentName}`] : []),
    ...resultLines(result, name)
  ].join('\n')
  return createContextAttachment({
    kind: 'scanResult',
    name: label,
    title: label,
    summary,
    url: result.checklistItemId ? checklistItemUrl(result.checklistItemId) : undefined,
    data: { ...resultData(result, name), source: 'WORD', document: documentName ?? null }
  })
}

/** A whole scan run (all its resolved results) as a single chat context attachment. */
export function buildScanRunAttachment(items: NamedScanResult[], documentName?: string): AgentAttachment {
  const ordered = orderByVerdict(items)
  const title = documentName ? `Scan results · ${documentName}` : `Scan results (${ordered.length})`
  const header = [
    DESCRIBE_RUN,
    '',
    ...(documentName ? [`Document: ${documentName}`] : []),
    `${ordered.length} checklist item${ordered.length === 1 ? '' : 's'} scanned (failed and errored items first)`
  ]
  const body = ordered.flatMap(({ result, name }, index) => ['', `[${index + 1}] ${name}`, ...resultLines(result, name).slice(1)])
  return createContextAttachment({
    kind: 'scanResult',
    name: title,
    title,
    summary: [...header, ...body].join('\n'),
    data: { source: 'WORD', document: documentName ?? null, results: ordered.map(({ result, name }) => resultData(result, name)) }
  })
}
